import React, { useState, useEffect } from 'react'
import { templateService } from '@templatean/shared/services'
import { Template, TemplateCategory } from '@templatean/shared/types'
import { outlookService } from '../services/outlookService'

// Category display names for the UI
const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  'document': 'Document',
  'presentation': 'Presentation',
  'email': 'Email',
  'spreadsheet': 'Spreadsheet',
  'form': 'Form',
  'report': 'Report',
  'contract': 'Contract',
  'proposal': 'Proposal',
  'invoice': 'Invoice',
  'other': 'Other'
}

const EmailTemplateLibrary: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([])
  const [categories] = useState<TemplateCategory[]>(['email'])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    try {
      const templatesData = await templateService.getTemplates({ category: 'email' })
      setTemplates(templatesData.items)
    } catch (error) {
      console.error('Error loading templates:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (template.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const handleUseTemplate = async (template: Template) => {
    try {
      // Parse template content (assuming it's stored as JSON)
      const content = JSON.parse(template.content.body)
      
      // Set subject if available
      if (content.subject) {
        await outlookService.setSubject(content.subject)
      }
      
      // Insert email body
      await outlookService.insertHtml(content.body, 'replace')
      
      alert('Template applied successfully!')
    } catch (error) {
      console.error('Error applying template:', error)
      alert('Failed to apply template')
    }
  }

  const handlePreviewTemplate = (template: Template) => {
    const content = JSON.parse(template.content.body)
    const previewWindow = window.open('', '_blank', 'width=600,height=800')
    if (previewWindow) {
      previewWindow.document.write(`
        <html>
          <head>
            <title>${template.name} - Preview</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .subject { font-weight: bold; margin-bottom: 20px; }
              .body { line-height: 1.6; }
            </style>
          </head>
          <body>
            ${content.subject ? `<div class="subject">Subject: ${content.subject}</div>` : ''}
            <div class="body">${content.body}</div>
          </body>
        </html>
      `)
      previewWindow.document.close()
    }
  }

  if (loading) {
    return <div className="loading">Loading templates...</div>
  }

  return (
    <div className="email-template-library">
      <div className="library-header">
        <h2>Email Templates</h2>
        <p>Choose from our collection of professional email templates</p>
      </div>

      <div className="library-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="category-filter">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-select"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {CATEGORY_LABELS[category]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="template-grid">
        {filteredTemplates.map(template => (
          <div key={template.id} className="template-card">
            <div className="template-header">
              <h3>{template.name}</h3>
              <span className="template-category">
                {CATEGORY_LABELS[template.category] || 'Uncategorized'}
              </span>
            </div>
            
            <p className="template-description">{template.description}</p>
            
            <div className="template-preview">
              {JSON.parse(template.content.body).subject && (
                <div className="preview-subject">
                  <strong>Subject:</strong> {JSON.parse(template.content.body).subject}
                </div>
              )}
              <div className="preview-snippet">
                {JSON.parse(template.content.body).body.substring(0, 150).replace(/<[^>]*>/g, '')}...
              </div>
            </div>

            {template.tags.length > 0 && (
              <div className="template-tags">
                {template.tags.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            )}

            <div className="template-actions">
              <button 
                onClick={() => handleUseTemplate(template)}
                className="btn btn-primary"
              >
                Use Template
              </button>
              <button 
                onClick={() => handlePreviewTemplate(template)}
                className="btn btn-secondary"
              >
                Preview
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="no-templates">
          <p>No templates found matching your criteria.</p>
        </div>
      )}
    </div>
  )
}

export default EmailTemplateLibrary